import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Code, Copy, Check, Clock, FileCode, Terminal, Database, Settings } from "lucide-react";
import { Highlight, themes } from "prism-react-renderer";

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:/, 'https:');

const LANGUAGES = [
  { id: 'yaml', name: 'YAML', icon: FileCode },
  { id: 'bash', name: 'Shell / Bash', icon: Terminal },
  { id: 'python', name: 'Python', icon: FileCode },
  { id: 'javascript', name: 'JavaScript', icon: FileCode },
  { id: 'typescript', name: 'TypeScript', icon: FileCode },
  { id: 'json', name: 'JSON', icon: Database },
  { id: 'sql', name: 'SQL', icon: Database },
  { id: 'dockerfile', name: 'Dockerfile', icon: FileCode },
  { id: 'nginx', name: 'Nginx', icon: Settings },
  { id: 'markup', name: 'HTML/XML', icon: FileCode },
  { id: 'css', name: 'CSS', icon: FileCode },
  { id: 'go', name: 'Go', icon: FileCode },
  { id: 'rust', name: 'Rust', icon: FileCode },
  { id: 'java', name: 'Java', icon: FileCode },
  { id: 'csharp', name: 'C#', icon: FileCode },
  { id: 'php', name: 'PHP', icon: FileCode },
  { id: 'ruby', name: 'Ruby', icon: FileCode },
  { id: 'hcl', name: 'Terraform (HCL)', icon: Settings },
  { id: 'ini', name: 'INI / Ansible', icon: Settings },
  { id: 'markdown', name: 'Markdown', icon: FileCode },
];

export default function PublicCodeSharePage() {
  const { snippetId } = useParams();
  const [snippet, setSnippet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadSnippet();
  }, [snippetId]);

  const loadSnippet = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/codeshare/public/${snippetId}`);
      
      if (response.ok) {
        const data = await response.json();
        setSnippet(data);
      } else if (response.status === 404) {
        setError('Snippet non trouvé ou non public');
      } else {
        setError('Erreur lors du chargement');
      }
    } catch (error) {
      console.error('Failed to load snippet:', error);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    if (!snippet) return;
    try {
      await navigator.clipboard.writeText(snippet.code);
      setCopied(true);
      toast.success('Code copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  const getLanguageInfo = (langId) => {
    return LANGUAGES.find(l => l.id === langId) || { id: langId, name: langId, icon: FileCode };
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Map some language IDs to Prism language names
  const getPrismLanguage = (langId) => {
    return {
      'hcl': 'javascript',
      'ini': 'ini',
      'dockerfile': 'docker',
      'nginx': 'nginx',
    }[langId] || langId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-white text-lg">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Code className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Oops !</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const langInfo = getLanguageInfo(snippet.language);
  const LangIcon = langInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2 text-white">
            <Code className="h-8 w-8" />
            <span className="text-2xl font-bold">CodeShare</span>
          </div>
        </div>

        {/* Snippet Card */}
        <Card className="shadow-2xl">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <LangIcon className="h-5 w-5" />
                  {snippet.title}
                </CardTitle>
                {snippet.description && (
                  <CardDescription className="mt-2">
                    {snippet.description}
                  </CardDescription>
                )}
              </div>
              <Button onClick={copyCode} variant="outline">
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? 'Copié !' : 'Copier le code'}
              </Button>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <Badge variant="secondary" className="flex items-center gap-1">
                <LangIcon className="h-3 w-3" />
                {langInfo.name}
              </Badge>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDate(snippet.createdAt)}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg overflow-hidden border">
              <Highlight 
                theme={themes.vsDark} 
                code={snippet.code || ''} 
                language={getPrismLanguage(snippet.language)}
              >
                {({ className, style, tokens, getLineProps, getTokenProps }) => (
                  <pre 
                    className="p-4 overflow-auto text-sm" 
                    style={{ 
                      ...style, 
                      margin: 0,
                      minHeight: '200px',
                      maxHeight: '600px',
                      fontFamily: "'Fira Code', 'Monaco', 'Consolas', monospace"
                    }}
                  >
                    {tokens.map((line, i) => (
                      <div key={i} {...getLineProps({ line })} className="table-row">
                        <span className="table-cell pr-4 text-gray-500 select-none text-right" style={{ minWidth: '2.5rem' }}>
                          {i + 1}
                        </span>
                        <span className="table-cell">
                          {line.map((token, key) => (
                            <span key={key} {...getTokenProps({ token })} />
                          ))}
                        </span>
                      </div>
                    ))}
                  </pre>
                )}
              </Highlight>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-slate-400 text-sm flex items-center justify-center gap-2">
          <span>Partagé via</span>
          <span className="font-semibold">DevOpsLearn CodeShare</span>
          <img src="/logo.png" alt="Takapes Learn" className="h-5 w-auto object-contain opacity-75 hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </div>
  );
}
